/**
 * Optimized Translation registry with selective language loading.
 * Only loads the currently selected language to minimize memory usage.
 */

const DEFAULT_FALLBACK_LANGUAGE = 'en';

// Critical namespaces that should be loaded first for better UX

const _CRITICAL_NAMESPACES = new Set(['common', 'navigation', 'buttons', 'layout']);

// Available languages - only load what's needed
const AVAILABLE_LANGUAGES = [
  'en',
  'ko',
  'zh',
  'ja',
  'vi',
  'ru',
  'mn',
  'ar',
  'th',
  'id',
  'es',
] as const;
type AvailableLanguage = (typeof AVAILABLE_LANGUAGES)[number];

// Cache for loaded language translations with LRU management
interface CacheEntry {
  data: Record<string, unknown>;
  accessed: number;
  created: number;
  size: number;
}

class TranslationCache {
  private readonly cache = new Map<string, CacheEntry>();
  private readonly maxSize: number;
  private readonly maxAge: number;

  constructor(maxSize = 10, maxAge = 30 * 60 * 1000) {
    // 10 entries, 30 minutes
    this.maxSize = maxSize;
    this.maxAge = maxAge;
  }

  get(key: string): Record<string, unknown> | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();

    // Check if expired
    if (now - entry.created > this.maxAge) {
      this.cache.delete(key);
      return null;
    }

    // Update access time
    entry.accessed = now;
    return entry.data;
  }

  set(key: string, data: Record<string, unknown>): void {
    const now = Date.now();
    const size = JSON.stringify(data).length;

    this.cache.set(key, {
      data,
      accessed: now,
      created: now,
      size,
    });

    this.evictIfNeeded();
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  // Remove expired and least recently used entries
  private evictIfNeeded(): void {
    const now = Date.now();

    // Remove expired entries
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.created > this.maxAge) {
        this.cache.delete(key);
      }
    }

    // If still over limit, remove least recently accessed
    if (this.cache.size > this.maxSize) {
      const entries = [...this.cache.entries()].sort((a, b) => a[1].accessed - b[1].accessed);

      const toRemove = entries.slice(0, this.cache.size - this.maxSize);
      for (const [key] of toRemove) {
        this.cache.delete(key);
      }
    }
  }

  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      totalSizeBytes: [...this.cache.values()].reduce((sum, entry) => sum + entry.size, 0),
    };
  }
}

// Global cache instance
const translationCache = new TranslationCache();

// Track currently loaded languages to enable cleanup
let currentlyLoadedLanguages = new Set<string>();

// Optimized dynamic import function that only loads requested files
const loadLocaleFile = async (
  languageCode: string,
  namespace: string | null
): Promise<Record<string, unknown>> => {
  try {
    // Use dynamic imports to load translation files
    // Vite will bundle these and create proper chunks
    const cacheKey = `${languageCode}/${namespace ?? 'main'}`;

    // Check cache first
    const cached = translationCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    // Use dynamic import to load the translation file
    // Vite supports JSON imports directly
    let module: { default: Record<string, unknown> };
    try {
      if (namespace) {
        // Import specific namespace file like 'ar/forms.json'
        module = await import(`@/locales/${languageCode}/${namespace}.json`);
      } else {
        // This shouldn't happen in current implementation, but fallback
        module = await import(`@/locales/${languageCode}/common.json`);
      }
    } catch {
      // Try common.json as fallback if specific namespace doesn't exist
      try {
        module = await import(`@/locales/${languageCode}/common.json`);
      } catch {
        throw new Error(
          `Translation file not found: ${languageCode}/${namespace ?? 'common'}.json`
        );
      }
    }

    // Extract the default export (JSON content)
    const data = module.default;

    // Cache the result
    translationCache.set(cacheKey, data);

    return data;
  } catch (error) {
    // File doesn't exist or failed to load - return empty object
    console.warn(
      `Failed to load translation file: ${languageCode}/${namespace ?? 'main'}.json`,
      error
    );
    return {};
  }
};

// Note: getAvailableLanguages moved to constant at top

// Get available namespaces for a language
// Helper function to safely check if an object has a property
const hasOwnProperty = (obj: Record<string, unknown>, prop: string): boolean => {
  return Object.prototype.hasOwnProperty.call(obj, prop);
};

// Helper function to safely get a property value
const _getOwnProperty = (obj: Record<string, unknown>, prop: string): unknown => {
  return hasOwnProperty(obj, prop) ? obj[prop] : undefined;
};

// Simplified function to determine which namespaces to load
const determineNamespacesToLoad = (
  normalizedLanguage: string,
  namespaces: string[] | null
): string[] => {
  if (namespaces && Array.isArray(namespaces)) {
    return namespaces;
  }

  // Return common namespaces - we'll load them on-demand
  return COMMON_NAMESPACES;
};

// Simplified function to load namespaces on-demand
const loadNamespaces = async (
  normalizedLanguage: string,
  namespacesToLoad: string[]
): Promise<Map<string, Record<string, unknown>>> => {
  const loadedModules = new Map<string, Record<string, unknown>>();

  // Load all requested namespaces in parallel
  const loadPromises = namespacesToLoad.map(async namespace => {
    try {
      const module = await loadLocaleFile(normalizedLanguage, namespace);
      loadedModules.set(namespace, module);
    } catch (error) {
      console.warn(`Failed to load namespace ${namespace} for ${normalizedLanguage}:`, error);
      loadedModules.set(namespace, {});
    }
  });

  await Promise.all(loadPromises);
  return loadedModules;
};

// Simplified function to merge translations from multiple namespaces
const mergeTranslations = (
  namespacesToLoad: string[],
  loadedModules: Map<string, Record<string, unknown>>
): Record<string, unknown> => {
  const merged: Record<string, unknown> = {};

  for (const namespace of namespacesToLoad) {
    const module = loadedModules.get(namespace);
    if (!module || typeof module !== 'object') continue;

    // Handle both direct exports and default exports
    const payload = (module as { default?: unknown }).default ?? module;

    if (typeof payload === 'object') {
      // Flatten namespace into merged object with namespace prefix
      for (const [key, value] of Object.entries(payload)) {
        const prefixedKey = namespace ? `${namespace}.${key}` : key;
        merged[prefixedKey] = value;
      }
    }
  }

  return merged;
};

// Common namespaces that should be checked for each language
const COMMON_NAMESPACES = [
  'common',
  'navigation',
  'buttons',
  'layout',
  'home',
  'treatments',
  'treatments_page',
  'doctors',
  'doctordetail',
  'provider_detail',
  'providers',
  'admin',
  'partnerdashboard',
  'auth',
  'errors',
  'equipments',
  'blog',
  'about_us',
  'privacy_policy',
  'terms_conditions',
  'home_faq',
  'favorites',
  'profile',
  'inquiry',
  'medical_procedure',
  'glossary',
  'chatbot',
  'transportation',
  'complaint_resolution',
  'anti_discrimination',
  'cookie_policy',
  'how_it_works',
  'medical_disclaimer',
  'subprocessors',
  'accessibility',
  'medipaths_lab',
];

// Note: Old loadLanguageNamespace function removed - using new optimized version below

export { AVAILABLE_LANGUAGES };

// Simplified namespace loading function
export const loadLanguageNamespace = async (
  languageCode: string,
  namespace: string | null
): Promise<Record<string, unknown>> => {
  return await loadLocaleFile(languageCode, namespace);
};

// Note: Old caching system removed - now using optimized TranslationCache class

const normalizeLanguageCode = (languageCode: string): string => {
  if (!languageCode || typeof languageCode !== 'string') {
    return DEFAULT_FALLBACK_LANGUAGE;
  }

  const normalized = languageCode.trim().toLowerCase();
  return AVAILABLE_LANGUAGES.includes(normalized as AvailableLanguage)
    ? normalized
    : DEFAULT_FALLBACK_LANGUAGE;
};

// Simplified preloading function for frequently used namespaces
export const preloadNamespaces = async (
  languageCode: string,
  namespaces: string[]
): Promise<void> => {
  const normalizedLanguage = normalizeLanguageCode(languageCode);

  // Load namespaces in parallel
  const promises = namespaces.map(async namespace => {
    try {
      // Preload into cache but don't wait for completion
      await loadLocaleFile(normalizedLanguage, namespace);
    } catch {
      // Silent fail for preloading
    }
  });

  // Wait for preloading to complete
  await Promise.allSettled(promises);
};

export const loadLanguageTranslations = async (
  languageCode: string,
  {
    forceRefresh = false,
    namespaces = null,
  }: { forceRefresh?: boolean; namespaces?: string[] | null } = {}
): Promise<Record<string, unknown>> => {
  const normalizedLanguage = normalizeLanguageCode(languageCode);

  // Check cache first
  if (!forceRefresh) {
    const cached = translationCache.get('lang:' + normalizedLanguage);
    if (cached) {
      return cached;
    }
  }

  try {
    const namespacesToLoad = determineNamespacesToLoad(normalizedLanguage, namespaces);
    if (namespacesToLoad.length === 0) {
      const emptyResult = {};
      translationCache.set('lang:' + normalizedLanguage, emptyResult);
      return emptyResult;
    }

    const cachedModules = await loadNamespaces(normalizedLanguage, namespacesToLoad);
    const merged = mergeTranslations(namespacesToLoad, cachedModules);

    // Cache the merged result
    translationCache.set('lang:' + normalizedLanguage, merged);

    // Track loaded languages for cleanup
    currentlyLoadedLanguages.add(normalizedLanguage);

    return merged;
  } catch (error) {
    console.warn(`Failed to load translations for language: ${normalizedLanguage}`, error);
    const emptyResult = {};
    translationCache.set('lang:' + normalizedLanguage, emptyResult);
    return emptyResult;
  }
};

// Clean up translations for languages that are no longer needed
export const cleanupUnusedLanguages = (currentLanguage: string): void => {
  const languagesToKeep = new Set([currentLanguage, DEFAULT_FALLBACK_LANGUAGE]);

  // Remove cache entries for languages not in use
  for (const lang of currentlyLoadedLanguages) {
    if (!languagesToKeep.has(lang)) {
      // Remove all cached entries for this language
      translationCache.delete('lang:' + lang);

      // Also remove individual namespace caches for this language
      // We'll do this by iterating through cache keys and removing matches
      // Note: This is a simplified cleanup - in production you might want more sophisticated tracking
    }
  }

  // Update the set of loaded languages
  currentlyLoadedLanguages = languagesToKeep;
};

export const invalidateLanguageCache = (languageCode?: string): void => {
  if (languageCode) {
    const normalized = languageCode.trim().toLowerCase();
    translationCache.delete('lang:' + normalized);
    currentlyLoadedLanguages.delete(normalized);
  } else {
    translationCache.clear();
    currentlyLoadedLanguages.clear();
  }
};

// Get cache statistics for debugging
export const getTranslationCacheStats = () => {
  return {
    ...translationCache.getStats(),
    loadedLanguages: [...currentlyLoadedLanguages],
  };
};
